import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { debounceTime, filter, switchMap, take, tap } from 'rxjs/operators';
import { BurdSameDayDeliveryService } from '@shared/same-day-delivery/burd-same-day-delivery.service';
import { SameDayDeliveryPlace, SameDayDeliveryStatus } from '@shared/same-day-delivery/same-day-delivery.model';
import { Observable } from 'rxjs';
import { BurdResponse } from '@features/checkout/checkout-delivery-burd/checkout-delivery-burd.models';
import { getSameDayDeliveryStatus, secondsLeftToDate } from '../same-day-delivery.helper';
import { NUM_OF_CHARS_FOR_SEARCH_ZIP_CODE } from '@constants/same-day-delivery.constants';
import { BurdDeliveryLeftTime } from '@models/api-orders.models';
import { GtmService } from '@core/services/gtm/gtm.service';
import { TranslateSafePipe } from '../../translate-safe/translate-safe.pipe';
import { SvgIconComponent } from '../../svg-icon/svg-icon.component';
import { InputZipcodeComponent } from '../../form-elements/input/input-zipcode/input-zipcode.component';
import { DatePipe, NgClass, NgIf, NgSwitch, NgSwitchCase, NgTemplateOutlet } from '@angular/common';
import { SameDayDeliveryCountDownComponent } from '../same-day-delivery-count-down/same-day-delivery-count-down.component';
import { LoadingDotsComponent } from '../../loading-dots/loading-dots.component';

@UntilDestroy()
@Component({
    selector: 'app-same-day-delivery',
    templateUrl: './same-day-delivery.component.html',
    styleUrls: ['./same-day-delivery.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        LoadingDotsComponent,
        SameDayDeliveryCountDownComponent,
        NgTemplateOutlet,
        InputZipcodeComponent,
        NgIf,
        NgClass,
        SvgIconComponent,
        NgSwitch,
        NgSwitchCase,
        FormsModule,
        ReactiveFormsModule,
        DatePipe,
        TranslateSafePipe
    ]
})
export class SameDayDeliveryComponent implements OnInit, AfterViewInit {
    private sameDayDeliveryTimeLeft?: BurdDeliveryLeftTime;
    public validating: boolean = false;
    public zipCodeFormControl: UntypedFormControl = new UntypedFormControl();
    public sameDayDeliveryStatus: SameDayDeliveryStatus = 'Idle';
    public zipCode?: string;
    public timeLeft?: Date;

    @Input() layout: 'little' | 'normal' = 'little';
    @Input() place: SameDayDeliveryPlace = 'pdp';

    constructor(
        private burdSameDayDeliveryService: BurdSameDayDeliveryService,
        private gtmService: GtmService,
        private cd: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.burdSameDayDeliveryService
            .getBurdDeliveryTimeLeft()
            .pipe(
                tap(data => (this.sameDayDeliveryTimeLeft = data)),
                take(1),
                untilDestroyed(this)
            )
            .subscribe(({ secondsLeft, onTimeForSameDayDelivery, dayOfWeek = 0, isHoliday }) => {
                this.timeLeft = secondsLeftToDate(secondsLeft);
                this.sameDayDeliveryStatus = getSameDayDeliveryStatus(dayOfWeek, isHoliday, onTimeForSameDayDelivery);
                this.cd.markForCheck();
            });

        this.zipCodeFormControl.valueChanges
            .pipe(
                debounceTime(250),
                filter(value => this.validateInputField(value?.toString() || '')),
                debounceTime(250),
                switchMap(value => this.validateZipCode(value)),
                untilDestroyed(this)
            )
            .subscribe(this.onZipCodeValidation.bind(this));
    }

    ngAfterViewInit(): void {
        const savedZipCode = this.burdSameDayDeliveryService.getSameDayDeliveryZipCode();
        if (savedZipCode) {
            this.zipCodeFormControl.setValue(savedZipCode);
        }
    }

    public onClearField = (): void => {
        this.zipCodeFormControl.setValue('');
        this.burdSameDayDeliveryService.setSameDayDeliveryZipCode('');
    };

    private validateInputField(value: string): boolean {
        if (value.length >= NUM_OF_CHARS_FOR_SEARCH_ZIP_CODE) {
            this.gtmService.trackSameDayDelivery(value, this.place);
            this.validating = true;
            return true;
        }

        this.validating = false;
        this.sameDayDeliveryStatus = 'Idle';
        this.burdSameDayDeliveryService.setSameDayDeliveryZipCode('');
        this.cd.markForCheck();
        return false;
    }

    private validateZipCode(value: string): Observable<BurdResponse> {
        return this.burdSameDayDeliveryService.getBurdZipApproval(value);
    }

    private onZipCodeValidation(response: BurdResponse): void {
        if (!this.sameDayDeliveryTimeLeft) {
            return;
        }

        const { deliverToZipCode } = response;
        const { onTimeForSameDayDelivery, isHoliday, dayOfWeek = 0 } = this.sameDayDeliveryTimeLeft;
        this.zipCode = this.zipCodeFormControl?.value || '';
        this.sameDayDeliveryStatus = getSameDayDeliveryStatus(
            dayOfWeek,
            isHoliday,
            onTimeForSameDayDelivery,
            deliverToZipCode
        );

        this.burdSameDayDeliveryService.setSameDayDeliveryZipCode(this.zipCode || '');
        this.validating = false;
        this.cd.markForCheck();
    }
}
